import { useState } from 'react';
import { WW_FY26_PROMOS, COLES_FY26_PROMOS } from '../data/promo-calendar';

const TH = { padding: '5px 6px', fontSize: 10, color: 'var(--text-muted)', fontWeight: 500, borderBottom: '1px solid var(--border)', textAlign: 'right', whiteSpace: 'nowrap' };
const TD = { padding: '4px 6px', fontSize: 11, textAlign: 'right', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
const LABEL_W = 110;
const CELL_W  = 68;

function getPromoTitle(week, retailer, filteredAssortments) {
  const promos = retailer === 'WW' ? WW_FY26_PROMOS : retailer === 'Coles' ? COLES_FY26_PROMOS : [];
  const w = String(week);
  const hits = promos
    .filter(p => !filteredAssortments || filteredAssortments.has(p.assortment))
    .flatMap(p => {
      const price  = p.data?.['PROMO PRICE']?.[w];
      const tactic = p.data?.['PROMO/MEDIA']?.[w] || p.data?.['TACTIC']?.[w];
      return (price || tactic) ? [`${p.assortment}: ${price || ''} ${tactic || ''}`.trim()] : [];
    });
  return hits.join('\n') || null;
}

function fmtN(v, metric) {
  if (v == null) return '—';
  if (metric === 'Units') {
    return v >= 1000 ? `${(v / 1000).toFixed(1)}K` : v.toLocaleString();
  }
  return v >= 1000000 ? `$${(v / 1e6).toFixed(2)}M` : `$${(v / 1000).toFixed(0)}K`;
}

function fmtPrice(v) {
  return v != null ? `$${v.toFixed(2)}` : '—';
}

const ROW_DEFS = [
  { key: 'actual',    label: 'Actual',     color: null },       // uses activeColor
  { key: 'mfgFcst',  label: 'Mfg Fcst',   color: '#f59e0b' },
  { key: 'custFcst', label: 'Cust Fcst',   color: '#06b6d4' },
  { key: 'avgPrice', label: 'Avg Price',   color: '#a855f7' },
];

export default function ScanDataTable({ weeks, metric, retailer, filteredAssortments, lastActualWeek, activeColor, showSku, skuItems }) {
  const [open, setOpen] = useState(true);
  const [showPast, setShowPast] = useState(false);

  // Only TY weeks (v > 0); optionally filter to future only
  const tyWeeks = weeks.filter(w => w.week > 0);
  const displayWeeks = showPast ? tyWeeks : tyWeeks.filter(w => w.week >= (lastActualWeek - 7));

  if (!displayWeeks.length) return null;

  const rows = ROW_DEFS.map(r => ({ ...r, color: r.key === 'actual' ? activeColor : r.color }));

  function renderTable(weekList, rowData) {
    return (
      <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid var(--border)' }}>
        <table style={{ borderCollapse: 'collapse', minWidth: `${LABEL_W + CELL_W * weekList.length}px` }}>
          <thead>
            <tr style={{ background: 'var(--bg-elevated)' }}>
              <th style={{ ...TH, width: LABEL_W, minWidth: LABEL_W, textAlign: 'left', position: 'sticky', left: 0, background: 'var(--bg-elevated)', zIndex: 2 }}>
                {metric}
              </th>
              {weekList.map(w => {
                const promoTitle = getPromoTitle(w.week, retailer, filteredAssortments);
                const isFcst = w.week > lastActualWeek;
                return (
                  <th key={w.week} title={promoTitle || undefined} style={{
                    ...TH,
                    width: CELL_W, minWidth: CELL_W,
                    color: isFcst ? 'var(--text-muted)' : promoTitle ? 'var(--warn)' : 'var(--text-muted)',
                    background: isFcst ? 'var(--bg-elevated)' : 'var(--bg-elevated)',
                    borderLeft: '1px solid var(--border)',
                    cursor: promoTitle ? 'help' : 'default',
                  }}>
                    {w.date}
                    {promoTitle && <div style={{ fontSize: 7, color: 'var(--warn)', marginTop: 1 }}>PROMO</div>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={row.key} style={{ background: ri % 2 === 0 ? 'var(--bg-elevated)' : 'var(--bg-elevated)' }}>
                <td style={{
                  ...TD, fontWeight: 600, fontSize: 11, textAlign: 'left',
                  color: row.color || 'var(--text-body)',
                  position: 'sticky', left: 0,
                  background: ri % 2 === 0 ? 'var(--bg-elevated)' : 'var(--bg-elevated)',
                  zIndex: 1, borderRight: '1px solid var(--border)',
                }}>
                  {row.label}
                </td>
                {weekList.map(w => {
                  const val = row.key === 'avgPrice' ? null : w[row.key];
                  const price = row.key === 'avgPrice' ? w.avgPrice : null;
                  const isFcst = w.week > lastActualWeek;
                  return (
                    <td key={w.week} style={{
                      ...TD,
                      background: ri % 2 === 0 ? (isFcst ? 'var(--bg-elevated)' : 'var(--bg-elevated)') : (isFcst ? 'var(--bg-elevated)' : 'var(--bg-elevated)'),
                      borderLeft: '1px solid var(--border)',
                      color: row.key === 'avgPrice'
                        ? (price != null ? 'var(--ai)' : 'var(--text-muted)')
                        : val != null ? row.color || activeColor : 'var(--text-muted)',
                    }}>
                      {row.key === 'avgPrice' ? fmtPrice(price) : fmtN(val, metric)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: '12px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: open ? 12 : 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => setOpen(o => !o)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, padding: 0,
          }}>
            {open ? '▾' : '▸'} Scan Data Table
          </button>
          {open && (
            <button onClick={() => setShowPast(p => !p)} style={{
              fontSize: 11, background: 'var(--border)', border: '1px solid var(--border-soft)',
              borderRadius: 6, padding: '2px 8px', cursor: 'pointer',
              color: showPast ? 'var(--info)' : 'var(--text-muted)',
            }}>
              {showPast ? 'All weeks' : 'Last 8W + Forecast'}
            </button>
          )}
        </div>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {open ? `${displayWeeks.length} weeks · hover column header for promo info` : ''}
        </span>
      </div>

      {open && !showSku && renderTable(displayWeeks, null)}

      {open && showSku && skuItems && skuItems.map(item => (
        <div key={item.label} style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--text-body)', fontWeight: 600, marginBottom: 4, paddingLeft: 2 }}>
            {item.label}
          </div>
          {renderTable(item.weeks.filter(w => w.week > 0 && (showPast || w.week >= (lastActualWeek - 7))), null)}
        </div>
      ))}
    </div>
  );
}
